/**
 * Client for the Tier 2 RSVP routes (docs/adr/002-tier2-contract.md §5 W1):
 * the member's own RSVP (structured wine, wine category, pay to confirm,
 * cancel), plus the admin side — register someone on their behalf, correct a
 * registration, and edit a tasting's wine categories.
 */
import {
  DEFAULT_INVITED_MEMBER_TYPES,
  WINE_COLORS,
  WINE_FIELD_LABELS,
  effectiveWineWorld,
  showsVarietal,
  type AdminRegisterInput,
  type AdminUpdateRegistrationInput,
  type CancelRsvpResponse,
  type EventWorldScope,
  type MemberType,
  type RegistrationAdminView,
  type RsvpCheckoutResult,
  type RsvpResponse,
  type RsvpSummaryView,
  type StructuredWineInput,
  type StructuredWineView,
  type WineCategoryInput,
  type WineCategoryView,
  type WineColor,
} from '@gwc/shared';
import { api } from '../lib/api';

/**
 * POST /events/:id/rsvp body. Omitted fields are left as they are; `wine` wins
 * over `wineName`. There is no guest count: members no longer bring guests
 * themselves (the server stores 0 for a new RSVP whatever is sent).
 */
export interface RsvpBody {
  wine?: StructuredWineInput;
  categoryId?: string | null;
  labelImageUrl?: string;
  notes?: string;
}

/** The override/notify flags default server-side, so they're optional here. */
type Defaulted = 'overrideCapacity' | 'overrideQuota';
export type AdminRegisterBody = Omit<AdminRegisterInput, Defaulted | 'notify'> &
  Partial<Pick<AdminRegisterInput, Defaulted | 'notify'>>;
export type AdminUpdateRegistrationBody = Omit<AdminUpdateRegistrationInput, Defaulted> &
  Partial<Pick<AdminUpdateRegistrationInput, Defaulted>>;

/**
 * Fill the Tier 2 fields an older API doesn't send yet, so the panel renders
 * the plain v1 RSVP rather than crashing on `summary.categories.map`.
 */
export function normalizeRsvpSummary(raw: Partial<RsvpSummaryView>): RsvpSummaryView {
  return {
    capacity: raw.capacity ?? null,
    seatsUsed: raw.seatsUsed ?? 0,
    spotsLeft: raw.spotsLeft ?? null,
    waitlistCount: raw.waitlistCount ?? 0,
    open: raw.open ?? false,
    myStatus: raw.myStatus ?? null,
    myGuests: raw.myGuests ?? 0,
    myWineName: raw.myWineName ?? null,
    myLabelImageUrl: raw.myLabelImageUrl ?? null,
    myNotes: raw.myNotes ?? null,
    registrationId: raw.registrationId ?? null,
    myPaymentStatus: raw.myPaymentStatus ?? null,
    myWine: raw.myWine ?? null,
    myWorld: raw.myWorld ?? null,
    myCategoryId: raw.myCategoryId ?? null,
    myWineReviewed: raw.myWineReviewed ?? false,
    registeredByAdmin: raw.registeredByAdmin ?? false,
    worldScope: raw.worldScope ?? 'both',
    rules: raw.rules ?? null,
    categories: raw.categories ?? [],
    invited: raw.invited ?? true,
    blockedReason: raw.blockedReason ?? null,
    payment: raw.payment ?? { required: false, price: null, chargeId: null, checkoutAvailable: false },
    cancellation: raw.cancellation ?? { cutoffDays: null, refundableUntil: null, refundable: true },
  };
}

export const rsvpApi = {
  summary: (eventId: string) =>
    api
      .get<Partial<RsvpSummaryView>>(`/events/${eventId}/rsvp/summary`)
      .then(normalizeRsvpSummary),
  /** Create, re-activate or update the caller's RSVP. */
  save: (eventId: string, body: RsvpBody) => api.post<RsvpResponse>(`/events/${eventId}/rsvp`, body),
  cancel: (eventId: string) => api.del<CancelRsvpResponse>(`/events/${eventId}/rsvp`),
  /** Start a card payment for the caller's unpaid seat; redirect to `checkoutUrl`. */
  checkout: (eventId: string) =>
    api
      .post<{ data: RsvpCheckoutResult }>(`/events/${eventId}/rsvp/checkout`, {})
      .then((r) => r.data),

  // ---- admin (tasting:run) ----
  registrations: (eventId: string) =>
    api
      .get<{ registrations: RegistrationAdminView[] }>(`/events/${eventId}/registrations`)
      .then((r) => r.registrations),
  register: (eventId: string, body: AdminRegisterBody) =>
    api
      .post<{ data: RegistrationAdminView }>(`/events/${eventId}/registrations`, body)
      .then((r) => r.data),
  updateRegistration: (eventId: string, registrationId: string, body: AdminUpdateRegistrationBody) =>
    api
      .patch<{ data: RegistrationAdminView }>(
        `/events/${eventId}/registrations/${registrationId}`,
        body,
      )
      .then((r) => r.data),

  // ---- wine categories ----
  categories: (eventId: string) =>
    api.get<{ data: WineCategoryView[] }>(`/events/${eventId}/wine-categories`).then((r) => r.data),
  /** Replace the set (tasting:create). Categories left out are deleted. */
  putCategories: (eventId: string, categories: WineCategoryInput[]) =>
    api
      .put<{ data: WineCategoryView[] }>(`/events/${eventId}/wine-categories`, { categories })
      .then((r) => r.data),
};

// --------------------------------------------------------------------------
// Form helpers shared by the RSVP panel, the admin registration dialog and the
// event setup form (kept here, not in the components, so those files export
// components only).
// --------------------------------------------------------------------------

/** A structured wine while it's being typed: everything optional. */
export type StructuredWineDraft = Partial<StructuredWineInput>;

export const WINE_COLOR_LABELS: Record<WineColor, string> = {
  red: 'Red',
  white: 'White',
  rose: 'Rosé',
  sparkling: 'Sparkling',
  orange: 'Orange',
  dessert: 'Dessert',
  other: 'Other',
};

export const isWineColor = (c: string | null | undefined): c is WineColor =>
  !!c && (WINE_COLORS as readonly string[]).includes(c);

/** "180.00" → "$180", "92.50" → "$92.50". */
export function money(price: string | number | null | undefined): string {
  const n = Number(price ?? 0);
  return `$${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2)}`;
}

/** "2026-10-06" → "Tue, Oct 6" (a calendar day; no timezone shift). */
export function shortDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** The form's starting point from a wine the API returned. */
export function draftFromView(view: StructuredWineView | null | undefined): StructuredWineDraft {
  if (!view) return {};
  return {
    world: view.world ?? undefined,
    vintage: view.vintage,
    producerId: view.producerId ?? undefined,
    producer: view.producer ?? '',
    regionId: view.regionId ?? undefined,
    region: view.region ?? '',
    vineyardId: view.vineyardId ?? undefined,
    vineyard: view.vineyard ?? '',
    varietalId: view.varietalId ?? undefined,
    varietal: view.varietal ?? '',
    cuvee: view.cuvee,
    color: isWineColor(view.color) ? view.color : undefined,
  };
}

/** Nothing typed yet — the wine hasn't been started. */
export function isDraftEmpty(d: StructuredWineDraft): boolean {
  return (
    d.vintage === undefined &&
    !d.producer?.trim() &&
    !d.region?.trim() &&
    !d.vineyard?.trim() &&
    !d.varietal?.trim() &&
    !d.cuvee?.trim()
  );
}

/**
 * What's still missing, in the member's words — or the finished wine. The
 * server checks the same things (`structuredWineInputSchema`); this just says
 * so before the round trip.
 */
export function completeWine(
  d: StructuredWineDraft,
  scope: EventWorldScope,
): { wine: StructuredWineInput; missing: [] } | { wine: null; missing: string[] } {
  const world = effectiveWineWorld(scope, d.world);
  const missing: string[] = [];
  if (!world) missing.push('Old or New World');
  const labels = WINE_FIELD_LABELS[world ?? 'new'];
  if (d.vintage === undefined) missing.push(`${labels.vintage} (or tick NV)`);
  if (!d.producer?.trim()) missing.push(labels.producer);
  if (!d.region?.trim()) missing.push(labels.region);
  if (!d.color) missing.push('Colour');
  if (missing.length || !world || d.vintage === undefined || !d.color) return { wine: null, missing };
  const keepVarietal = showsVarietal(world) && !!d.varietal?.trim();
  return {
    wine: {
      world,
      vintage: d.vintage,
      producerId: d.producerId,
      producer: d.producer!.trim(),
      regionId: d.regionId,
      region: d.region!.trim(),
      vineyardId: d.vineyard?.trim() ? d.vineyardId : undefined,
      vineyard: d.vineyard?.trim() || undefined,
      varietalId: keepVarietal ? d.varietalId : undefined,
      varietal: keepVarietal ? d.varietal!.trim() : undefined,
      cuvee: d.cuvee?.trim() || undefined,
      color: d.color,
    },
    missing: [],
  };
}

/** Drop blank category rows and number the rest in screen order, ready to send. */
export function categoriesForSave(value: WineCategoryInput[]): WineCategoryInput[] {
  return value
    .filter((c) => c.name.trim())
    .map((c, position) => ({
      ...(c.id ? { id: c.id } : {}),
      name: c.name.trim(),
      maxWines: c.maxWines ?? null,
      color: c.color ?? null,
      position,
    }));
}

/** The Tier 2 part of a tasting's setup, as the setup form edits it. */
export interface EventSetupValue {
  worldScope: EventWorldScope;
  rules: string;
  invitedMemberTypes: MemberType[];
  requiresPayment: boolean;
  price: number | null;
  requiresDuesPaid: boolean;
  cancellationCutoffDays: number | null;
  wineCategories: WineCategoryInput[];
}

export function defaultEventSetup(): EventSetupValue {
  return {
    worldScope: 'both',
    rules: '',
    invitedMemberTypes: [...DEFAULT_INVITED_MEMBER_TYPES],
    requiresPayment: false,
    price: null,
    requiresDuesPaid: false,
    cancellationCutoffDays: null,
    wineCategories: [],
  };
}

export function eventSetupFromEvent(
  event: {
    worldScope?: EventWorldScope;
    rules?: string | null;
    invitedMemberTypes?: MemberType[];
    requiresPayment?: boolean;
    requiresDuesPaid?: boolean;
    cancellationCutoffDays?: number | null;
    price?: string | null;
  },
  categories: WineCategoryView[] = [],
): EventSetupValue {
  const d = defaultEventSetup();
  return {
    worldScope: event.worldScope ?? d.worldScope,
    rules: event.rules ?? '',
    invitedMemberTypes: event.invitedMemberTypes?.length
      ? event.invitedMemberTypes
      : d.invitedMemberTypes,
    requiresPayment: event.requiresPayment ?? false,
    price: event.price != null && event.price !== '' ? Number(event.price) : null,
    requiresDuesPaid: event.requiresDuesPaid ?? false,
    cancellationCutoffDays: event.cancellationCutoffDays ?? null,
    wineCategories: categories.map((c) => ({
      id: c.id,
      name: c.name,
      maxWines: c.maxWines,
      color: isWineColor(c.color) ? c.color : null,
      position: c.position,
    })),
  };
}
